import { DEV } from 'cc/env';
import { Animation, Node, randomRange, _decorator } from 'cc';
import Lobby from '../../../../System/Lobby';
import JackpotRecord from './JackpotRecord';
import ItemGenerator from '../../../../../common/scripts/Toolkit/Components/Misc/ItemGenerator';
import Processor from '../../../../../common/scripts/Toolkit/Components/Misc/Processor';
import Delay from '../../../../../common/scripts/Toolkit/Utils/Delay';
import Page from '../../../../../common/scripts/Toolkit/Components/Page/Page';
import DataType from '../../../../../tableEngine/Script/Game/Data/Config/DataStructure';
import JackpotModel from '../../../../../tableEngine/Script/Game/Data/Model/LobbyJackpotModel';
import NodeName from '../../../Data/Config/SceneNodeName';

const { ccclass, property, requireComponent } = _decorator;

@ccclass('JackpotRecordBar')
@requireComponent(Animation)
export default class JackpotRecordBar extends Processor {
    @property({ tooltip: DEV && '每次更新的間隔時間(毫秒)' })
    private readonly intervalInMs = 5000;

    @property
    private readonly maxDisplayCount = 4;

    @property
    private readonly totalDisplayCount = 20;

    @property
    private readonly switchAnimationName = '';

    @property
    private readonly resetAnimationName = '';

    private _animation: Animation = null;
    private _itemGenerator: ItemGenerator = null;
    private _jackpotPage: Page = null;
    private _jackpotRecordMap: Map<Node, JackpotRecord> = new Map();
    private _displayRecordDataList: DataType.Jackpot.WinRecordData[] = [];

    private _currntStartIndex = 0;
    private _isMoving = false;
    private _lightSweepAnimationPromise: Promise<void> = null;

    private get animation() {
        if (!this._animation) {
            this._animation = this.getComponent(Animation);
        }
        return this._animation;
    }

    private get itemGenerator() {
        if (!this._itemGenerator) {
            this._itemGenerator = this.getComponentInChildren(ItemGenerator);
        }
        return this._itemGenerator;
    }

    private get jackpotPage() {
        if (!this._jackpotPage) {
            this._jackpotPage = Lobby.scene.getNode(NodeName.JackpotPage).getComponent(Page);
        }
        return this._jackpotPage;
    }

    private get currentStartIndex() {
        return this._currntStartIndex;
    }
    private set currentStartIndex(value: number) {
        if (value === this._currntStartIndex) {
            return;
        }
        this._currntStartIndex = value;
        this._updateDisplay();
    }

    private get displayCount() {
        return this.maxDisplayCount - 1;
    }

    protected onLoad() {
        Lobby.jackpot.on(JackpotModel.EVENT_TYPE.JACKPOT_RECORD_RECEIVED, () => {
            if (this.node.activeInHierarchy && !this.isRunning) {
                this._updateDataList();
                this.play();
            }
        });
        this.node.on(Node.EventType.TOUCH_END, () => this.jackpotPage.open());
        Lobby.jackpot.syncLatestWinRecords();
        this._playLightSweepAnimation();
    }

    protected onEnable() {
        if (this._displayRecordDataList.length < this.maxDisplayCount) {
            this._updateDataList();
        }
        this.play();
    }

    protected onDisable() {
        this.stop();
    }

    protected *run() {
        this._updateDisplay();
        if (this._displayRecordDataList.length >= this.maxDisplayCount) {
            while (true) {
                yield Delay.resolve(this.intervalInMs);
                if (this._lightSweepAnimationPromise) {
                    yield this._lightSweepAnimationPromise;
                    yield Delay.resolve(randomRange(500, 1000));
                }
                this.animation.play(this.switchAnimationName);
                this._isMoving = true;
                yield this.animation.waitForFinished(this.switchAnimationName);
                this._isMoving = false;
                if (this.currentStartIndex >= Math.min(this._displayRecordDataList.length, this.totalDisplayCount) - this.displayCount) {
                    this._updateDataList();
                    this.currentStartIndex = 0;
                } else {
                    ++this.currentStartIndex;
                }
                this.animation.play(this.resetAnimationName);
            }
        }
    }

    protected async finish() {
        this.animation.play(this.resetAnimationName);
    }

    private _updateDisplay() {
        this.itemGenerator.items.forEach((item, idx) => {
            const data = this._getDataOfIndex(idx);
            if (data) {
                const jackpotRecord =
                    this._jackpotRecordMap.get(item) ??
                    (() => {
                        const temp = item.getComponent(JackpotRecord);
                        this._jackpotRecordMap.set(item, temp);
                        return temp;
                    })();
                jackpotRecord.display(data);
            }
        });
    }

    private _updateDataList() {
        const winRecords = Lobby.jackpot.getWinRecords();
        const originalDataList = [...this._displayRecordDataList];
        this._displayRecordDataList = winRecords.slice(0, this.totalDisplayCount).reverse();
        // encure continuous data display if the display is full
        if (originalDataList.length) {
            this._displayRecordDataList.unshift(...originalDataList.slice(-(this.displayCount - 1)));
        }
        this.itemGenerator.displayItemCount = Math.min(this._displayRecordDataList.length, this.maxDisplayCount);
    }

    private _getDataOfIndex(index: number): DataType.Jackpot.WinRecordData {
        const dataIndex = (this.currentStartIndex + index) % this._displayRecordDataList.length;
        return this._displayRecordDataList[dataIndex];
    }

    private async _playLightSweepAnimation() {
        while (true) {
            await Delay.resolve(randomRange(3000, 4000));
            if (!this.node.activeInHierarchy || this._isMoving) {
                continue;
            }
            this._lightSweepAnimationPromise = this.itemGenerator.items
                .slice(0, this.maxDisplayCount - 1)
                .reduce(
                    (promise, item) =>
                        promise.then(() => {
                            const jackpotRecord = this._jackpotRecordMap.get(item);
                            jackpotRecord.playAnimation();
                            return Delay.resolve(200);
                        }),
                    Promise.resolve(),
                )
                // Wait for the last animation to finish
                .then(() => Delay.resolve(1230));
            await this._lightSweepAnimationPromise;
            this._lightSweepAnimationPromise = null;
        }
    }
}
